import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// The organisation's time zone (backend core/timezone.py): every time on screen is
// shown in it, whatever the viewer's own clock says. The auth provider sets it.
let zoneName: string | undefined

/** design.md §5: dates render as `06 Oct 2026`, times as `06 Oct 2026, 14:05`. */
const dateFormat = (timeZone?: string) =>
  new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone })
const dateTimeFormat = (timeZone?: string) =>
  new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone,
  })

// A plain date ("2026-10-06") is a day, not an instant: read it in UTC so no zone moves it.
const DATE = dateFormat('UTC')
let DATE_TIME = dateTimeFormat()
let INSTANT_DATE = dateFormat()

export function setTimeZone(name: string | null | undefined) {
  if ((name || undefined) === zoneName) return
  zoneName = name || undefined
  DATE_TIME = dateTimeFormat(zoneName)
  INSTANT_DATE = dateFormat(zoneName)
}

const PLAIN_DATE = /^\d{4}-\d{2}-\d{2}$/

export function formatDate(value: string | null | undefined) {
  if (!value) return null
  return (PLAIN_DATE.test(value) ? DATE : INSTANT_DATE).format(new Date(value))
}

export function formatDateTime(value: string | null | undefined) {
  if (!value) return null
  return DATE_TIME.format(new Date(value))
}

/** The organisation's wall clock now, as a local Date whose year, month, day, hour and
 *  minute are the zone's — for anything that asks what "today" is. */
export function zoneNow(): Date {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: zoneName,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date())
      .map((p) => [p.type, Number(p.value)]),
  )
  return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second)
}

/**
 * design.md §4 `RelativeTime`: short relative label, exact value on hover.
 * Deliberately coarse — minutes matter, seconds do not.
 */
export function relativeTime(value: string | null | undefined): string | null {
  if (!value) return null
  const then = new Date(value).getTime()
  const seconds = Math.round((Date.now() - then) / 1000)
  const future = seconds < 0
  const abs = Math.abs(seconds)

  let label: string
  if (abs < 45) label = 'just now'
  else if (abs < 90) label = '1m'
  else if (abs < 3600) label = `${Math.round(abs / 60)}m`
  else if (abs < 86400) label = `${Math.round(abs / 3600)}h`
  else if (abs < 2592000) label = `${Math.round(abs / 86400)}d`
  else if (abs < 31536000) label = `${Math.round(abs / 2592000)}mo`
  else label = `${Math.round(abs / 31536000)}y`

  if (label === 'just now') return label
  return future ? `in ${label}` : `${label} ago`
}

/** design.md §4: a date as days from today in the organisation's calendar — "today",
 *  "yesterday", "3d ago", "in 2d", then months and years. */
export function relativeDay(value: string | null | undefined): string | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}/.test(value)) return null
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  const now = zoneNow()
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  const days = Math.round((today - Date.UTC(y, m - 1, d)) / 86_400_000)
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days === -1) return 'tomorrow'
  const abs = Math.abs(days)
  const label = abs < 30 ? `${abs}d` : abs < 365 ? `${Math.round(abs / 30)}mo` : `${Math.round(abs / 365)}y`
  return days < 0 ? `in ${label}` : `${label} ago`
}

/** Initials for Avatar. Falls back to the email's first character. */
export function initials(name: string, email?: string) {
  const source = name?.trim() || email?.split('@')[0] || '?'
  const parts = source.split(/[\s._-]+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** design.md §5: an empty value is an em dash, never a blank cell. */
export const EMPTY = '—'
